import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import ReactMarkdown from "react-markdown";
import { BookOpen, Calendar, AppWindow, Loader2, ChevronDown, Info, ArrowLeft } from "lucide-react";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Guias() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState("");
  const [guia, setGuia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingGuia, setLoadingGuia] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/guias`)
      .then(({ data }) => {
        setList(data);
        const first = data.find((g) => g.disponible);
        if (first) {
          setSelected(first.plataforma);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selected) return;
    setLoadingGuia(true);
    setGuia(null);
    axios
      .get(`${API}/guias/${selected}`)
      .then(({ data }) => setGuia(data))
      .catch(() => setGuia(null))
      .finally(() => setLoadingGuia(false));
  }, [selected]);

  if (!user?.es_pro) {
    return (
      <div className="px-6 py-20 max-w-2xl mx-auto text-center">
        <Info className="w-10 h-10 mx-auto text-slate-400" />
        <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">Solo disponible con el Módulo Pro</h1>
        <p className="mt-2 text-slate-600">Las guías de implementación son parte del paquete Pro. Desbloquéalo desde tu cuenta.</p>
        <Link to="/cuenta" className="mt-6 inline-flex h-12 px-6 rounded-lg bg-[#1E3A8A] text-white items-center font-semibold">Ir a Mi cuenta</Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <Loader2 className="w-7 h-7 text-[#1E3A8A] animate-spin" />
      </div>
    );
  }

  return (
    <>
      <SEO title="Guías de implementación" description="Paso a paso para cumplir la Ley 21.719 según la plataforma de tu sitio." path="/guias" noindex />
      <section className="bg-ink-950 text-white relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-grid-pattern opacity-30 pointer-events-none" />
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-blue-600/10 blur-3xl pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 pt-16 pb-12 lg:pt-20">
          <Link to="/cuenta" className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-blue-300 hover:text-white mb-8">
            <ArrowLeft className="w-3 h-3" />
            Mi cuenta
          </Link>
          <div className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] text-blue-300 uppercase mb-5">
            <span className="inline-block w-6 h-px bg-blue-400/60" />
            Acceso Pro · Guías
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-white leading-[1.05] tracking-tight">
            Implementación <em className="italic text-blue-300">paso a paso</em>.
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl">
            Cómo configurar tu sitio para cumplir con la Ley 21.719, según la plataforma que usas.
          </p>
        </div>
      </section>

      <section className="px-6 py-12 sm:py-16">
        <div className="max-w-4xl mx-auto">
          {/* Dropdown */}
          <div>
            <label htmlFor="platform" className="block font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500 mb-3">
              Elige tu plataforma
            </label>
            <div className="relative">
              <select
                id="platform"
                data-testid="platform-select"
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
                className="w-full h-12 pl-12 pr-10 bg-white border border-slate-300 rounded-xl focus:border-blue-600 focus:ring-2 focus:ring-blue-100 focus:outline-none appearance-none font-medium text-slate-900 transition-all"
              >
                {list.map((g) => (
                  <option key={g.plataforma} value={g.plataforma} disabled={!g.disponible}>
                    {g.nombre_publico} {!g.disponible ? "— Próximamente" : ""}
                  </option>
                ))}
              </select>
              <AppWindow className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
              <ChevronDown className="w-5 h-5 text-slate-400 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>

          {/* Guía */}
          <div className="mt-10">
            {loadingGuia ? (
              <Loader2 className="w-6 h-6 text-blue-700 animate-spin" />
            ) : guia ? (
              <article data-testid="guia-content" className="bg-white border border-slate-200 rounded-2xl p-7 sm:p-10">
                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-4">
                  <Calendar className="w-3 h-3" />
                  Última revisión · {guia.fecha_revision}
                </div>
                <div className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] text-blue-700 uppercase">
                  <BookOpen className="w-3 h-3" />
                  {guia.nombre_publico}
                </div>
                <h2 className="mt-4 font-display text-2xl sm:text-3xl text-slate-900 tracking-tight leading-tight">{guia.titulo}</h2>
                <p className="mt-3 text-slate-600 leading-relaxed">{guia.resumen}</p>

                <ol className="mt-8 space-y-5">
                  {guia.pasos.map((p, i) => (
                    <li key={i} className="flex gap-4">
                      <div className="w-9 h-9 rounded-xl bg-slate-900 text-white font-mono text-sm grid place-items-center shrink-0 tabular-nums">
                        {String(i + 1).padStart(2, "0")}
                      </div>
                      <div>
                        <h3 className="font-display text-lg text-slate-900">{p.titulo}</h3>
                        <p className="mt-1 text-sm sm:text-base text-slate-600 leading-relaxed">{p.detalle}</p>
                      </div>
                    </li>
                  ))}
                </ol>

                {guia.apps_recomendadas?.length > 0 && (
                  <div className="mt-10 p-5 rounded-2xl bg-blue-50/60 border border-blue-100">
                    <p className="font-mono text-[10px] tracking-[0.22em] text-blue-700 uppercase mb-2">
                      Apps / Plugins recomendados
                    </p>
                    <p className="text-sm text-slate-700">{guia.apps_recomendadas.join(" · ")}</p>
                  </div>
                )}
                {guia.notas && (
                  <p className="mt-6 text-xs text-slate-500 leading-relaxed border-t border-slate-100 pt-4">
                    <strong className="text-slate-700">Nota:</strong> {guia.notas}
                  </p>
                )}
              </article>
            ) : (
              <p className="text-slate-500">Selecciona una plataforma.</p>
            )}
          </div>

          <p className="mt-10 text-xs text-slate-500 leading-relaxed">
            Los menús de las plataformas pueden cambiar entre versiones. Revisamos estas guías periódicamente; si alguna instrucción no coincide con lo que ves, consulta la documentación oficial vigente de la plataforma.
          </p>
        </div>
      </section>
    </>
  );
}
